import { useState, useEffect, useRef } from 'react'
import './InnovationParallax.css'

type ParallaxItem = {
  number: string
  title: string
  description: string
  imageUrl: string
}

const ITEMS: ParallaxItem[] = [
  {
    number: '01',
    title: 'Quality Inspection Reporting',
    description:
      'Our supervisors perform digital inspections to maintain spotless results and track service benchmarks.',
    imageUrl: '/innovation/inspection.png'
  },
  {
    number: '02',
    title: 'Time & Attendance Software',
    description:
      "Staff check-in via mobile apps, so you always know who's on-site and when.",
    imageUrl: '/innovation/attendance.png'
  },
  {
    number: '03',
    title: 'Real-Time Communication Platforms',
    description:
      'Instant messaging tools connect our teams, ensuring quick issue resolution and updates from field to office.',
    imageUrl: '/innovation/communication.png'
  },
  {
    number: '04',
    title: 'Safety & Compliance Assurance',
    description:
      'Our team performs real-time safety checks, incident reporting, and compliance audits to ensure every site meets health standards and regulatory requirements.',
    imageUrl: '/innovation/compliance.png'
  }
]

const InnovationParallax = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0) // Start with point 1 (index 0)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [imageLoaded, setImageLoaded] = useState<boolean>(true) // Start with image loaded for default
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset animation state first
            setIsVisible(false)
            // Small delay to ensure the reset is processed, then trigger animation
            setTimeout(() => {
              setIsVisible(true)
            }, 50)
          } else {
            // Reset when scrolling out of view
            setIsVisible(false)
          }
        })
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -100px 0px' // Start animation slightly before fully in view
      }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <div className="ip-wrap" ref={sectionRef}>
      <div className="ip-stage">
        <div className="ip">
          <div className="ip-left">
            <div className="ip-hero" data-active-point={activeIndex}>
              <img 
                src={ITEMS[activeIndex].imageUrl} 
                alt={ITEMS[activeIndex].title}
                className={`ip-image ${imageLoaded ? 'loaded' : ''}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(false)}
              />
            </div>
          </div>
          <div className="ip-right">
            {ITEMS.map((item, i) => (
              <div 
                className={`ip-item ${i === activeIndex ? 'active' : ''} ${isVisible ? 'animate-in' : ''}`} 
                key={item.number}
                onMouseEnter={() => {
                  setActiveIndex(i)
                  setImageLoaded(false) // Reset image loading state for smooth transition
                }}
                onMouseLeave={() => {
                  // Don't reset to -1, maintain the last hovered point
                  // Keep imageLoaded as true to maintain image visibility
                }}
                style={{ 
                  animationDelay: `${i * 0.2}s` // Stagger each item by 0.2s
                }}
              >
                <div className="ip-number">{item.number}</div>
                <div className="ip-content">
                  <h3 className="ip-title">{item.title}</h3>
                  <p className="ip-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InnovationParallax


